import { nextTick } from 'vue'

/**
 * Finds the actual scrollable container for comments
 * @param startElement - The element to start searching from (usually comments_cards)
 * @returns The scrollable container element or null
 */
export function findScrollableContainer(
  startElement: HTMLElement | null
): HTMLElement | null {
  if (!startElement) return null

  let scrollContainer = startElement

  // Check if the start element itself is scrollable
  if (startElement.scrollHeight <= startElement.clientHeight) {
    // Look for the first scrollable parent
    let parent = startElement.parentElement
    while (parent) {
      if (
        parent.scrollHeight > parent.clientHeight &&
        (getComputedStyle(parent).overflowY === 'auto' ||
          getComputedStyle(parent).overflowY === 'scroll')
      ) {
        scrollContainer = parent
        break
      }
      parent = parent.parentElement
    }
  }

  return scrollContainer
}

/**
 * Scrolls to a specific element within its scrollable container
 * @param targetElement - The element to scroll to
 * @param options - Scroll options
 */
export async function scrollToElement(
  targetElement: HTMLElement,
  options: {
    behavior?: ScrollBehavior
    block?: 'start' | 'center' | 'end'
    padding?: number
    containerElement?: HTMLElement | null
  } = {}
): Promise<void> {
  const {
    behavior = 'smooth',
    block = 'start',
    padding = 20,
    containerElement = null,
  } = options

  // Find the scrollable container
  const startElement =
    containerElement || document.getElementById('comments_cards')
  const scrollContainer = findScrollableContainer(startElement)

  if (!scrollContainer || !targetElement) return

  // Wait for next tick to ensure DOM is ready
  await nextTick()

  // Get current positions
  const containerRect = scrollContainer.getBoundingClientRect()
  const targetRect = targetElement.getBoundingClientRect()

  let targetScrollTop: number

  switch (block) {
    case 'center':
      // Center the element in the viewport
      targetScrollTop =
        scrollContainer.scrollTop +
        (targetRect.top - containerRect.top) -
        containerRect.height / 2 +
        targetRect.height / 2
      break
    case 'end':
      // Align bottom of element with bottom of viewport
      targetScrollTop =
        scrollContainer.scrollTop +
        (targetRect.bottom - containerRect.bottom) +
        padding
      break
    case 'start':
    default:
      // Align top of element with top of viewport (with padding)
      targetScrollTop =
        scrollContainer.scrollTop +
        (targetRect.top - containerRect.top) -
        padding
      break
  }

  // Ensure we don't scroll past bounds
  const maxScroll = scrollContainer.scrollHeight - scrollContainer.clientHeight
  const finalScrollTop = Math.max(0, Math.min(targetScrollTop, maxScroll))

  scrollContainer.scrollTo({
    top: finalScrollTop,
    behavior,
  })
}

/**
 * Scrolls to show a comment editor with consideration for button area
 * @param editorElement - The comment editor element
 * @param options - Additional options
 */
export async function scrollToCommentEditor(
  editorElement: HTMLElement,
  options: {
    buttonAreaHeight?: number
    behavior?: ScrollBehavior
    delay?: number
  } = {}
): Promise<void> {
  const { buttonAreaHeight = 80, behavior = 'smooth', delay = 300 } = options

  const scrollContainer = findScrollableContainer(
    document.getElementById('comments_cards')
  )
  if (!scrollContainer || !editorElement) return

  // Wait for content to render
  await nextTick()

  // Additional delay for editor content to fully render
  await new Promise(resolve => setTimeout(resolve, delay))

  // Get current positions
  const containerRect = scrollContainer.getBoundingClientRect()
  const editorRect = editorElement.getBoundingClientRect()

  // Calculate the full height of the editor element
  const editorFullHeight = editorElement.offsetHeight

  // Check if editor needs scrolling
  const bottomOutOfView =
    editorRect.top + editorFullHeight + buttonAreaHeight > containerRect.bottom
  const topOutOfView = editorRect.top < containerRect.top

  if (bottomOutOfView || topOutOfView) {
    let targetScrollTop: number

    if (editorFullHeight + buttonAreaHeight > containerRect.height) {
      // Editor is too large, align to top with padding
      targetScrollTop =
        scrollContainer.scrollTop + (editorRect.top - containerRect.top) - 20
    } else {
      // Editor fits, center it in view
      targetScrollTop =
        scrollContainer.scrollTop +
        (editorRect.top - containerRect.top) -
        containerRect.height / 2 +
        (editorFullHeight + buttonAreaHeight) / 2
    }

    // Ensure we don't scroll past bounds
    const maxScroll =
      scrollContainer.scrollHeight - scrollContainer.clientHeight
    targetScrollTop = Math.max(0, Math.min(targetScrollTop, maxScroll))

    scrollContainer.scrollTo({
      top: targetScrollTop,
      behavior,
    })
  }
}

/**
 * Scrolls to the comments section (useful for new comments)
 * @param options - Scroll options
 */
export async function scrollToCommentsSection(
  options: {
    behavior?: ScrollBehavior
    padding?: number
    delay?: number
  } = {}
): Promise<void> {
  const { behavior = 'smooth', padding = 20, delay = 300 } = options

  // Give time for Vue to render and update the DOM
  await new Promise(resolve => setTimeout(resolve, delay))

  const commentsCards = document.getElementById('comments_cards')
  const scrollContainer = findScrollableContainer(commentsCards)

  if (!scrollContainer || !commentsCards) return

  // Get the position of the comments_cards element relative to the scroll container
  const commentsCardsRect = commentsCards.getBoundingClientRect()
  const scrollContainerRect = scrollContainer.getBoundingClientRect()

  // Calculate where to scroll to show the top of comments with some padding
  const targetScrollTop =
    scrollContainer.scrollTop +
    (commentsCardsRect.top - scrollContainerRect.top) -
    padding

  scrollContainer.scrollTo({
    top: targetScrollTop,
    behavior,
  })
}

/**
 * Adds a scale animation to an element
 * @param element - The element to animate
 * @param options - Animation options
 */
export function addScaleAnimation(
  element: HTMLElement,
  options: {
    initialScale?: number
    finalScale?: number
    duration?: number
    delay?: number
  } = {}
): void {
  const {
    initialScale = 0.98,
    finalScale = 1,
    duration = 300,
    delay = 0,
  } = options

  setTimeout(() => {
    element.style.transform = `scale(${initialScale})`
    element.style.transition = `transform ${duration}ms`

    setTimeout(() => {
      element.style.transform = `scale(${finalScale})`
    }, duration)
  }, delay)
}

/**
 * Highlights a comment with animation
 * @param commentElement - The comment element to highlight
 */
export async function highlightComment(
  commentElement: HTMLElement
): Promise<void> {
  // Use our unified scroll function
  await scrollToElement(commentElement, {
    block: 'center',
    behavior: 'smooth',
  })

  // Add a subtle highlight animation
  addScaleAnimation(commentElement, {
    initialScale: 1.02,
    finalScale: 1,
    duration: 300,
  })
}
